import { useAccessPendingAction } from "./use-access-pending-action";
import { useEffectEvent, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { UserMinus } from "lucide-react";
import { FilterChip } from "@/components/design/FilterChip";
import { Select } from "@/components/design/Select";
import { Tooltip } from "@/components/common/Tooltip";
import { Button } from "@/components/design/Button";
import { TextInput } from "@/components/design/TextField";
import { PersonLink } from "@/components/common/PersonLink";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { AccessGroupMembersDocument, AccessGroupPeopleDocument, AddAccessGroupMemberDocument,
  RemoveAccessGroupMemberDocument, StringFilterCaseSensitivity, type AccessGroupDetailsFragment,
  type AccessGroupMembersQuery } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";

type Member = AccessGroupMembersQuery["accessGroupMembers"]["members"][number];

export function AccessGroupMembersPanel({ group, onChanged }: {
  group: AccessGroupDetailsFragment; onChanged: () => Promise<void>;
}) {
  const pendingAction = useAccessPendingAction(`access-members-${group.id}`);
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const { data, loading, error, refetch, fetchMore } = useQuery(AccessGroupMembersDocument, {
    variables: { groupId: group.id }, fetchPolicy: "network-only", errorPolicy: "all", notifyOnNetworkStatusChange: true,
  });
  const [sourceFilter, setSourceFilter] = useState("ALL");
  const [sort, setSort] = useState("nameAsc");
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  useEffect(() => { const timer = setTimeout(() => setTerm(search.trim()), 250); return () => clearTimeout(timer); }, [search]);
  const people = useQuery(AccessGroupPeopleDocument, { skip: term.length < 2 || !group.canManageMembers,
    variables: { input: { first: 20, where: { formattedName: {
      contains: term, caseSensitivity: StringFilterCaseSensitivity.CaseInsensitive,
    } } } }, fetchPolicy: "no-cache" });
  const [add, { loading: adding }] = useMutation(AddAccessGroupMemberDocument);
  const [remove, { loading: removing }] = useMutation(RemoveAccessGroupMemberDocument);
  const [confirmation, setConfirmation] = useState<Member | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const inFlight = useRef(false);
  const result = data?.accessGroupMembers;
  const busy = adding || removing || pendingAction.pending;

  async function change(personId: string, addingMember: boolean) {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const variables = { input: { groupId: group.id, personId } };
      if (addingMember) await add({ variables }); else await remove({ variables });
      setConfirmation(null); setSearch(""); setTerm("");
      await Promise.all([refetch(), onChanged()]);
      setFailure(null);
    } catch (cause) { setFailure(userErrorMessage(intl, cause)); throw cause; }
    finally { inFlight.current = false; }
  }

  function loadMore() {
    if (loading || inFlight.current || !result?.hasNextPage || !result.nextAfter) return;
    inFlight.current = true;
    return fetchMore({ variables: { after: result.nextAfter }, updateQuery(previous, { fetchMoreResult }) {
        const page = fetchMoreResult.accessGroupMembers;
        const members = new Map(previous.accessGroupMembers.members.map((item) => [item.personId, item]));
        page.members.forEach((item) => members.set(item.personId, item));
        return { ...previous, accessGroupMembers: { ...page, members: [...members.values()] } };
      } }).then(() => setFailure(null))
      .catch(cause => setFailure(userErrorMessage(intl, cause)))
      .finally(() => { inFlight.current = false; });
  }

  const continueLoading = useEffectEvent(() => { void loadMore(); });

  // Load bounded pages before declaring a filter complete: a match may be on a later page.
  useEffect(() => {
    if (result?.hasNextPage && !loading && !failure && !error) continueLoading();
  }, [result?.hasNextPage, result?.nextAfter, loading, failure, error]);

  const matchesSource = (member: Member, source: string) => member.memberships.some(item => item.source === source);
  const members = (result?.members ?? []).filter(member => sourceFilter === "ALL" || matchesSource(member, sourceFilter))
    .sort((left, right) => {
      if (sort === "manualFirst" || sort === "identityFirst") {
        const source = sort === "manualFirst" ? "MANUAL" : "IDENTITY";
        const priority = Number(matchesSource(right, source)) - Number(matchesSource(left, source));
        if (priority) return priority;
      }
      const byName = (left.person?.formattedName ?? "").localeCompare(right.person?.formattedName ?? "", intl.locale,
        { sensitivity: "base", numeric: true }) || left.personId.localeCompare(right.personId);
      return sort === "nameDesc" ? -byName : byName;
    });

  return <div className="space-y-4">
    {pendingAction.label && <p role="status" className="text-smycles-body font-medium">{pendingAction.label}</p>}
    <h3 className="text-smycles-body font-medium">{t("admin.groups.members")}</h3>
    <p className="text-smycles-meta text-ink-muted">{t(group.orgUnitId ? "admin.groups.departmentHint" : "admin.groups.sourcesHint")}</p>
    {group.canManageMembers && <div id="group-members-add" className="space-y-2">
      <label className="block"><span className="smycles-field-label">{t("admin.groups.addMember")}</span>
        <TextInput id="group-member-search" type="search" value={search} disabled={busy} placeholder={t("admin.groups.searchPeople")}
          onChange={(event) => setSearch(event.target.value)} /></label>
      {people.loading && <p role="status" className="text-smycles-meta text-ink-muted">{t("common.loading")}</p>}
      {term.length >= 2 && people.data?.persons.nodes.map((person) => <div key={person.id} className="flex items-center justify-between gap-4 py-2">
        <PersonLink size="md" className="min-w-0 text-smycles-body rounded focus-visible:ring-2 focus-visible:ring-ink-ui"
          person={{ id: person.id, name: person.formattedName || t("admin.roles.unnamedPerson"),
            avatarContent: person.avatar?.content, avatarMime: person.avatar?.mimeType }}
          secondary={person.emailPrimary && <span title={t("admin.groups.primaryEmail")}>{person.emailPrimary}</span>} />
        <Button id={`group-member-add-${person.id}`} variant="secondary" size="compact" disabled={busy} onClick={() => pendingAction.stage(`${t("common.add")}: ${person.formattedName}`, () => change(person.id, true))}>{t("common.add")}</Button>
      </div>)}
      {term.length >= 2 && !people.loading && people.data?.persons.nodes.length === 0 &&
        <p className="text-smycles-meta text-ink-muted">{t("admin.groups.noPeople")}</p>}
      {term.length >= 2 && people.data?.persons.pageInfo.hasNextPage &&
        <p className="text-smycles-meta text-ink-muted">{t("admin.groups.refineSearch")}</p>}
    </div>}
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div role="group" aria-label={t("admin.groups.sourceFilter")} className="flex flex-wrap gap-2">
        {["ALL", "MANUAL", "IDENTITY", ...(group.orgUnitId ? ["ORGANIZATION"] : [])].map(source =>
          <FilterChip key={source} id={`group-members-filter-${source.toLowerCase()}`} active={sourceFilter === source}
            onClick={() => setSourceFilter(source)}>
            {t({ ALL: "admin.groups.allSources", MANUAL: "admin.groups.manual", IDENTITY: "admin.groups.identity",
              ORGANIZATION: "admin.groups.organization" }[source]!)}
          </FilterChip>)}
      </div>
      <label className="flex flex-wrap items-center gap-2 text-smycles-meta text-ink-muted">
        <span>{t("admin.groups.memberSort")}</span>
        <Select id="group-members-sort" variant="pill" hideEmpty value={sort} onChange={setSort} options={[
          { value: "nameAsc", label: t("admin.groups.nameAscending") },
          { value: "nameDesc", label: t("admin.groups.nameDescending") },
          { value: "manualFirst", label: t("admin.groups.manualFirst") },
          { value: "identityFirst", label: t("admin.groups.identityFirst") },
        ]} />
      </label>
    </div>
    {(error || failure || people.error) && <p role="alert" className="text-smycles-body text-ink">
      {failure ?? userErrorMessage(intl, error ?? people.error)}</p>}
    {loading && <p role="status" className="text-smycles-body text-ink-muted">{t("common.loading")}</p>}
    <ul id="group-members-list" className="@container divide-y divide-ink/10">{members.map((member) => <li key={member.personId}
      className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 py-3 @min-[28rem]:grid-cols-[minmax(0,1fr)_auto_auto]">
      <PersonLink id={`group-member-profile-${member.personId}`} size="md" className="max-w-full text-smycles-body rounded focus-visible:ring-2 focus-visible:ring-ink-ui"
        person={{ id: member.personId, name: member.person?.formattedName || t("admin.roles.unnamedPerson"),
          avatarContent: member.person?.avatar?.content, avatarMime: member.person?.avatar?.mimeType }}
        secondary={member.person?.emailPrimary && <span id={`group-member-email-${member.personId}`} title={t("admin.groups.primaryEmail")}>{member.person.emailPrimary}</span>} />
        <p id={`group-member-source-${member.personId}`} className="col-start-1 row-start-2 pl-11 text-smycles-meta text-ink-muted @min-[28rem]:col-start-2 @min-[28rem]:row-start-1 @min-[28rem]:pl-0">{member.memberships.map((source) =>
          t({ MANUAL: "admin.groups.manual", IDENTITY: "admin.groups.identity",
            ORGANIZATION: "admin.groups.organization" }[source.source])).join(" · ")}</p>
      {member.memberships.some((source) => source.source === "MANUAL") && <div className="col-start-2 row-start-1 @min-[28rem]:col-start-3"><Tooltip content={t(member.memberships.some((source) => source.canRemove) ? "admin.groups.removeManual" : "admin.groups.lastAdministrator")}>
        <Button id={`group-member-remove-${member.personId}`} variant="icon" size="compact"
          aria-label={t("admin.groups.removeManual")} disabled={busy || !member.memberships.some((source) => source.canRemove)} onClick={() => setConfirmation(member)}>
          <UserMinus size={16} aria-hidden="true" />
        </Button>
      </Tooltip></div>}
    </li>)}</ul>
    {!loading && !error && result?.members.length === 0 && <p id="group-members-empty" className="text-smycles-body text-ink-muted">{t("admin.groups.noMembers")}</p>}
    {!loading && !error && !result?.hasNextPage && !!result?.members.length && members.length === 0 &&
      <p className="text-smycles-body text-ink-muted">{t("admin.groups.noMatchingMembers")}</p>}
    {result?.hasNextPage && <p role="status" className="text-smycles-meta text-ink-muted">{t("admin.groups.loadingAllMembers")}</p>}
    {failure && result?.hasNextPage && <Button variant="secondary" size="compact" disabled={loading}
      onClick={() => void loadMore()}>{t("admin.roles.retry")}</Button>}
    <ConfirmDialog id="group-member-remove-dialog" open={!!confirmation} title={t("admin.groups.removeManual")}
      message={t("admin.groups.removeHint")} onCancel={() => { if (!busy) setConfirmation(null); }}
      onConfirm={() => { if (confirmation) { const member = confirmation; pendingAction.stage(`${t("admin.groups.removeManual")}: ${member.person?.formattedName ?? member.personId}`, () => change(member.personId, false)); setConfirmation(null); } }} />
  </div>;
}
